import { supabase } from '../../config/supabase';
import { ProjectColumn } from '../../types/projects';
import { RealtimeChannel } from '@supabase/supabase-js';

const defaultColumns: ProjectColumn[] = [
  { id: 'col-1', title: 'À faire', cards: [] },
  { id: 'col-2', title: 'En cours', cards: [] },
  { id: 'col-3', title: 'Terminé', cards: [] }
];

export const projectsService = {
  async initialize(): Promise<ProjectColumn[]> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', 'board-state')
      .single();

    if (error || !data) {
      // Create default state
      const projectsData = {
        columns: defaultColumns.map(column => ({
          id: column.id,
          title: column.title,
          cards: column.cards.map(card => ({
            id: card.id,
            title: card.title,
            content: card.content,
            label: card.label,
            dueDate: card.dueDate || null,
            tasks: card.tasks || []
          }))
        })),
        updatedAt: new Date().toISOString()
      };

      await supabase.from('projects').upsert({
        id: 'board-state',
        ...projectsData
      });
      return defaultColumns;
    }

    if (!data.columns) return defaultColumns;

    return data.columns.map((column: any) => ({
      id: column.id || `col-${Math.random().toString(36).substr(2, 9)}`,
      title: column.title || 'Untitled Column',
      cards: (column.cards || []).map((card: any) => ({
        id: card.id || `card-${Math.random().toString(36).substr(2, 9)}`,
        title: card.title,
        content: card.content || '',
        label: card.label,
        dueDate: card.dueDate || null,
        tasks: card.tasks || []
      }))
    }));
  },

  subscribe(
    onSuccess: (columns: ProjectColumn[]) => void,
    onError: (error: Error) => void
  ): () => void {
    let channel: RealtimeChannel | null = null;

    const setupSubscription = async () => {
      // Initial fetch
      try {
        const columns = await this.initialize();
        onSuccess(columns);
      } catch (error) {
        onError(error as Error);
        return;
      }

      // Subscribe to changes
      channel = supabase
        .channel('projects-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'projects',
            filter: 'id=eq.board-state'
          },
          async () => {
            try {
              const columns = await this.initialize();
              onSuccess(columns);
            } catch (error) {
              onError(error as Error);
            }
          }
        )
        .subscribe();
    };

    setupSubscription();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  },

  async saveAll(columns: ProjectColumn[]): Promise<void> {
    const projectsData = {
      columns: columns.map(column => ({
        id: column.id,
        title: column.title,
        cards: column.cards.map(card => ({
          id: card.id,
          title: card.title,
          content: card.content,
          label: card.label,
          dueDate: card.dueDate || null,
          tasks: card.tasks || []
        }))
      })),
      updatedAt: new Date().toISOString()
    };

    const { error } = await supabase.from('projects').upsert({
      id: 'board-state',
      ...projectsData
    });

    if (error) {
      console.error('Erreur de sauvegarde:', error);
      throw new Error('Erreur lors de la sauvegarde du tableau');
    }
  }
};
